import { useEffect, useRef } from "react";
import { heroBgImage } from "@/mocks/images";

export default function Hero() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.querySelectorAll("[data-animate]").forEach((child, i) => {
              setTimeout(() => {
                child.classList.add("animate-fade-in-up");
                child.classList.remove("opacity-0");
              }, i * 150);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      ref={heroRef}
      className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden bg-[#0a0a0f]"
    >
      <div className="absolute inset-0">
        <img
          src={heroBgImage}
          alt="SaaS technology background"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#0a0a0f]/60" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/40 via-transparent to-[#0a0a0f]/70" />
      </div>

      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:32px_32px]" />

      <div className="relative z-10 text-center section-padding w-full">
        <div className="max-w-4xl mx-auto">
          <div className="opacity-0" data-animate>
            <span className="inline-block px-4 py-1.5 bg-emerald-500/15 border border-emerald-500/25 rounded-full text-emerald-400 text-xs md:text-sm font-medium tracking-wide mb-6">
              SaaS Development Studio
            </span>
          </div>

          <h1
            className="opacity-0 font-outfit font-bold text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight mb-6"
            data-animate
          >
            Build SaaS.
            <br />
            <span className="gradient-text">Ship Faster.</span>
          </h1>

          <p
            className="opacity-0 text-base md:text-lg text-white/60 max-w-2xl mx-auto mb-10 leading-relaxed"
            data-animate
          >
            SaaS ForceKeys Services builds powerful <strong className="text-white/90">SaaS applications</strong> for modern teams. Our flagship product,{" "}
            <strong className="text-white/90">Translate Cloud API</strong>, unifies Google, Azure, AWS, DeepL and ForceKeys Cloud into one powerful <strong className="text-white/90">translation API</strong>.
          </p>

          <div
            className="opacity-0 flex flex-col sm:flex-row items-center justify-center gap-4"
            data-animate
          >
            <a
              href="https://translate.saas.forcekeys.com"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="px-8 py-3.5 bg-emerald-600 text-white rounded-full font-semibold text-sm md:text-base hover:bg-emerald-700 transition-all duration-300 shadow-lg shadow-emerald-600/20 whitespace-nowrap"
            >
              Explore Translate Cloud API
            </a>
            <a
              href="#translate-cloud"
              className="px-8 py-3.5 bg-white/10 text-white rounded-full font-semibold text-sm md:text-base hover:bg-white/20 transition-all duration-300 border border-white/20 whitespace-nowrap"
            >
              See Features
            </a>
          </div>

          <div
            className="opacity-0 mt-16 md:mt-20 flex items-center justify-center gap-8 md:gap-12"
            data-animate
          >
            <div className="text-center">
              <div className="font-outfit font-bold text-2xl md:text-3xl text-white">
                5
              </div>
              <div className="text-xs md:text-sm text-white/50 mt-1">
                Cloud Providers
              </div>
            </div>
            <div className="w-px h-10 bg-white/15" />
            <div className="text-center">
              <div className="font-outfit font-bold text-2xl md:text-3xl text-white">
                120+
              </div>
              <div className="text-xs md:text-sm text-white/50 mt-1">
                Languages
              </div>
            </div>
            <div className="w-px h-10 bg-white/15" />
            <div className="text-center">
              <div className="font-outfit font-bold text-2xl md:text-3xl text-white">
                50M+
              </div>
              <div className="text-xs md:text-sm text-white/50 mt-1">
                Translations
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
